import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Briefcase,
  MessageSquare,
  User,
  Newspaper,
  Gift,
  Settings,
  LogOut,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  ExternalLink,
} from 'lucide-react';
import { ProfileData } from '@/components/profile/ProfileContext';

interface DashboardSidebarProps {
  profileData: ProfileData | null;
  hasProfileData: boolean;
  isProfileComplete: boolean;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
  profileData,
  hasProfileData,
  isProfileComplete,
}) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  // Calculate profile completion percentage
  const getProfileCompletionPercentage = () => {
    if (!profileData) return 0;
    const sections = Object.values(profileData);
    const completedSections = sections.filter(section => 
      section && Object.keys(section).length > 0
    );
    return Math.round((completedSections.length / sections.length) * 100);
  };

  // Check Zerodha connection status (simplified for now)
  const isZerodhaConnected = () => {
    // This would check actual connection status from your state/API
    return localStorage.getItem('kite_access_token') !== null;
  };

  const menuItems = [
    {
      title: "Dashboard",
      icon: LayoutDashboard,
      path: "/dashboard",
      isActive: location.pathname === '/dashboard'
    },
    {
      title: "Portfolio",
      icon: Briefcase,
      path: "/portfolio",
      isActive: location.pathname.startsWith('/portfolio')
    },
    {
      title: "AI Assistant",
      icon: MessageSquare,
      path: "/dashboard?tab=ai",
      isActive: location.search.includes('tab=ai')
    },
    {
      title: "Profile Setup",
      icon: User,
      path: "/profile-setup",
      isActive: location.pathname === '/profile-setup',
      badge: !isProfileComplete ? "Incomplete" : undefined
    },
    {
      title: "News & Insights",
      icon: Newspaper,
      path: "/top-5-news",
      isActive: location.pathname === '/top-5-news'
    },
    {
      title: "Credits & Referrals",
      icon: Gift,
      path: "/credits-referrals",
      isActive: location.pathname === '/credits-referrals'
    }
  ];

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.user_metadata?.avatar_url} />
            <AvatarFallback className="bg-[#9E7C47] text-black text-sm font-medium">
              {user?.email?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {user?.user_metadata?.full_name || user?.email}
            </p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Quick Actions - Prioritizing Zerodha Connection */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[#9E7C47] font-semibold">
            Quick Actions
          </SidebarGroupLabel>
          <SidebarGroupContent className="space-y-2">
            {/* Zerodha Connection Card - Top Priority */}
            <div className="p-3 bg-gradient-to-r from-[#9E7C47]/10 to-[#F9F1A9]/10 rounded-lg border border-[#9E7C47]/20">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-[#9E7C47]" />
                  <span className="text-sm font-medium">Zerodha</span>
                </div>
                {isZerodhaConnected() ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                )}
              </div>
              <p className="text-xs text-gray-300 mb-3">
                {isZerodhaConnected() 
                  ? "Portfolio synced successfully" 
                  : "Connect to get personalized insights"}
              </p>
              {!isZerodhaConnected() && (
                <Button
                  size="sm"
                  className="w-full bg-gradient-to-r from-[#9E7C47] to-[#F9F1A9] hover:from-[#8A6B3A] hover:to-[#9E7C47] text-black font-medium"
                  onClick={() => navigate('/profile-setup?step=4')}
                >
                  Connect Now
                  <ExternalLink className="ml-2 h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Profile Completion Card */}
            <div className="p-3 bg-black/20 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Profile Setup</span>
                <Badge variant={isProfileComplete ? "default" : "destructive"} className="text-xs">
                  {getProfileCompletionPercentage()}%
                </Badge>
              </div>
              <Progress 
                value={getProfileCompletionPercentage()} 
                className="h-2 mb-2" 
              />
              <p className="text-xs text-gray-300 mb-2">
                {isProfileComplete ? "Profile complete!" : "Complete your profile for better insights"}
              </p>
              {!isProfileComplete && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border-[#9E7C47] text-[#9E7C47] hover:bg-[#9E7C47] hover:text-black"
                  onClick={() => navigate('/profile-setup')}
                >
                  Continue Setup
                  <ChevronRight className="ml-2 h-3 w-3" />
                </Button>
              )}
            </div>
          </SidebarGroupContent>
        </SidebarGroup>

        <Separator className="my-4 bg-gray-700" />

        {/* Navigation Menu */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-gray-400">Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <Link to={item.path}>
                    <SidebarMenuButton 
                      isActive={item.isActive}
                      className="group hover:bg-[#9E7C47]/20 hover:text-[#F9F1A9] data-[active=true]:bg-[#9E7C47] data-[active=true]:text-black w-full"
                    >
                      <item.icon className="h-4 w-4" />
                      <span className="flex-1">{item.title}</span>
                      {item.badge && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {item.badge}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  </Link>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start border-gray-700 hover:bg-[#9E7C47]/20 hover:border-[#9E7C47]"
            >
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            align="end" 
            className="w-56 bg-black border-gray-700"
            side="top"
          >
            <DropdownMenuItem 
              onClick={() => navigate('/profile-setup')}
              className="cursor-pointer hover:bg-[#9E7C47]/20"
            >
              <User className="mr-2 h-4 w-4" />
              Profile Settings
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={handleSignOut}
              className="cursor-pointer text-red-400 hover:bg-red-500/20"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default DashboardSidebar;